import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { cn } from '@/lib/cn';
import { Logo } from './Logo';

const links = [
  { to: '/', label: 'Home', anchor: undefined },
  { to: '/#creazioni', label: 'Creazioni' },
  { to: '/#pasticceria', label: 'La pasticceria' },
  { to: '/ordina', label: 'Ordina' },
] as const;

export function Navbar() {
  const { pathname, hash } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  // Hero is light (cream) → navbar always uses ink/green, never white.
  const overHero = false;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname, hash]);

  return (
    <>
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-500',
          scrolled ? 'border-b border-line bg-cream/85 py-3 backdrop-blur-md' : 'border-b border-transparent py-5',
        )}
      >
        <nav
          className={cn(
            'u-container flex items-center justify-between transition-colors duration-300',
            overHero ? 'text-white' : 'text-ink',
          )}
        >
          <Link to="/" aria-label="Rachele Dolcezze — home">
            <Logo className="h-9 md:h-11" light={overHero} />
          </Link>

          <div className="hidden items-center gap-9 text-sm uppercase tracking-[0.16em] md:flex">
            {links.map((l) => (
              <Link key={l.label} to={l.to} className="link-line opacity-80 transition-opacity hover:opacity-100">
                {l.label}
              </Link>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center md:hidden"
            aria-label={open ? 'Chiudi menu' : 'Apri menu'}
            aria-expanded={open}
          >
            <span className="relative block h-3 w-6">
              <span className={cn('absolute left-0 h-px w-6 bg-current transition-all duration-300', open ? 'top-1.5 rotate-45' : 'top-0')} />
              <span className={cn('absolute left-0 top-1.5 h-px w-6 bg-current transition-opacity duration-300', open && 'opacity-0')} />
              <span className={cn('absolute left-0 h-px w-6 bg-current transition-all duration-300', open ? 'top-1.5 -rotate-45' : 'top-3')} />
            </span>
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-sage-deep px-8 text-cream md:hidden"
          >
            <ul className="space-y-6">
              {links.map((l, i) => (
                <motion.li
                  key={l.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link to={l.to} className="font-display text-4xl">
                    {l.label}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
